/** Strip non-hex chars and return uppercase hex string. */
export function sanitizeHex(raw) {
  return String(raw ?? "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();
}

/** Split hex into byte pairs for chip display. */
export function hexToBytes(hex, maxBytes = null) {
  const clean = sanitizeHex(hex);
  if (!clean) return [];
  const pairs = clean.match(/.{1,2}/g) ?? [];
  const sliced = maxBytes != null ? pairs.slice(0, maxBytes) : pairs;
  return sliced.map((b) => (b.length === 1 ? `0${b}` : b));
}

/** Pad or trim data bytes to match DLC. */
export function dataBytesForDlc(hex, dlc) {
  const bytes = hexToBytes(hex);
  const n = Math.max(0, Math.min(8, Number(dlc) || 0));
  const out = bytes.slice(0, n);
  while (out.length < n) out.push("00");
  return out;
}

function toHexLE(num, byteLen) {
  let n = Math.max(0, Number(num) || 0);
  const out = [];
  for (let i = 0; i < byteLen; i++) {
    out.push((n & 0xff).toString(16).toUpperCase().padStart(2, "0"));
    n >>= 8;
  }
  return out.join("");
}

function formatHexSpaced(flat) {
  const clean = sanitizeHex(flat);
  if (!clean) return "—";
  return (clean.match(/.{1,2}/g) ?? []).join(" ");
}

/** Encoded packet + parsed fields for Command Preview panel. */
export function buildCommandPacket({ baudrate, cmdCount, slots }) {
  const structure = [];

  structure.push({ label: "Header", value: "4C4135" });
  structure.push({ label: "Beacon Type", value: "6A00" });
  structure.push({ label: "Sequence ID", value: "0100" });
  structure.push({ label: "Payload Length", value: toHexLE(slots.length * 22 + 8, 2) });
  structure.push({ label: "Number of Modules", value: "01" });
  structure.push({ label: "Module Length", value: "2A00" });
  structure.push({ label: "Coproc Sequence ID", value: "0100" });
  structure.push({ label: "TX Module", value: "0500" });
  structure.push({ label: "Baudrate", value: toHexLE(baudrate, 1) });
  structure.push({ label: "Number of Commands", value: String(cmdCount).padStart(2, "0") });

  const totalTimeout = slots.reduce((max, s) => Math.max(max, parseInt(s.timeoutMs, 10) || 0), 0);
  structure.push({ label: "Total Command Timeout", value: toHexLE(totalTimeout, 4) });

  slots.forEach((slot, i) => {
    const n = i + 1;
    const arb = hexToBytes(slot.arbId, slot.extended ? 4 : 2);
    const arbVal = arb.join("").padEnd(8, "0").slice(0, 8) || "00000000";
    const data = dataBytesForDlc(slot.data, slot.dlc);
    const dataVal = [...data, ...Array(Math.max(0, 8 - data.length)).fill("00")].join("");

    structure.push({ label: `ARB ID ${n}`, value: arbVal });
    structure.push({ label: `Timeout ${n}`, value: toHexLE(parseInt(slot.timeoutMs, 10) || 0, 3) });
    structure.push({ label: `DLC ${n}`, value: String(slot.dlc).padStart(2, "0") });
    structure.push({ label: `Count ${n}`, value: String(slot.count || "1").padStart(2, "0") });
    structure.push({ label: `Data ${n}`, value: dataVal });
  });

  structure.push({ label: "CRC", value: "2645" });

  const flat = structure.map((s) => s.value).join("");
  return { rawHex: formatHexSpaced(flat), structure };
}

export function defaultCommandSlot(index) {
  return {
    arbId: index === 0 ? "12323213" : "",
    extended: true,
    dlc: 4,
    data: index === 0 ? "2131231231232131" : "",
    timeoutMs: "100000",
    count: "1",
  };
}

export const MODEL_OPTIONS = [
  { id: "daly_48v", label: "Daly_48V_2022_V1" },
  { id: "daly_72v", label: "Daly_72V_2023_V2" },
  { id: "lg_96v", label: "LG_96V_Pro_V1" },
];

export const BAUD_OPTIONS = [125, 250, 500];
export const CMD_COUNT_OPTIONS = [1, 2, 3, 4];

export const COMMAND_TYPES = [
  {
    id: "can",
    label: "CAN command",
    description: "Configure CAN bus frames, baudrate, and preview the encoded packet.",
    icon: "chip",
  },
  {
    id: "device-pin",
    label: "Device pin based",
    description: "Send commands by controlling device GPIO pins.",
    icon: "device",
  },
  {
    id: "custom",
    label: "Custom command",
    description: "Define a custom command with free-form configuration.",
    icon: "terminal",
  },
];

export function getCommandTypeLabel(typeId) {
  return COMMAND_TYPES.find((t) => t.id === typeId)?.label ?? typeId ?? "—";
}

function formatCommandHex(hex) {
  const clean = String(hex ?? "").replace(/\s/g, "").toUpperCase();
  return clean || "—";
}

/** Packet preview from a saved command hex string. */
export function packetFromSavedCommand(command) {
  const clean = formatCommandHex(command?.commandHex);
  const rawHex = clean === "—" ? "—" : (clean.match(/.{1,2}/g) ?? []).join(" ");
  return { rawHex, structure: [] };
}

/** Detail-panel field rows for a saved command. */
export function buildCommandDetailFields(command) {
  if (!command) return [];
  return [
    { label: "Command Name", value: command.name, icon: "commands" },
    { label: "Description", value: command.description, icon: "layers" },
    { label: "Command Type", value: getCommandTypeLabel(command.commandType), icon: "layers" },
    { label: "Model Name", value: command.model, icon: "model" },
    { label: "Baudrate", value: command.baud ? `${command.baud} kbps` : "", icon: "activity" },
    { label: "Created By", value: command.createdBy, icon: "users" },
    { label: "Saved At", value: command.dateTime, icon: "calendar" },
    { label: "Command Hex", value: formatCommandHex(command.commandHex), icon: "commands", mono: true },
  ];
}

export function formatCommandDateTime(iso) {
  const d = iso ? new Date(iso) : new Date();
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, ".");
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  return `${date} | ${time}`;
}
